/**
 * VIP VAPE — Analytics Layer
 * ---------------------------------------------------------------
 * Fires GA4 events for all upsell interactions so you can measure
 * attach rate, AOV lift, and conversion vs UpsellPlus claims.
 *
 * Events (GA4):
 *   - vv_upsell_view       (impression)
 *   - vv_upsell_click      (click on Add button)
 *   - vv_upsell_add        (successfully added to cart)
 *   - vv_ab_assigned       (A/B variant assigned)
 *
 * Public API:
 *   VVAnalytics.track(eventName, params)
 *   VVAnalytics.trackImpression(product, surface)
 *   VVAnalytics.trackClick(product, surface)
 *   VVAnalytics.trackAdd(product, surface)
 *   VVAnalytics.getVariant(experimentKey, variants)
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  const COOKIE_NAME = 'vv_visitor_id';
  const AB_COOKIE = 'vv_ab_buckets';
  const COOKIE_DAYS = 90;

  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  }

  function getOrCreateVisitorId() {
    let id = getCookie(COOKIE_NAME);
    if (!id) {
      id = 'vv_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      setCookie(COOKIE_NAME, id, COOKIE_DAYS);
    }
    return id;
  }

  // FNV-1a hash for deterministic A/B bucketing
  function hashString(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return Math.abs(h);
  }

  function track(eventName, params) {
    const visitor_id = getOrCreateVisitorId();
    const payload = Object.assign({
      visitor_id,
      timestamp: Date.now(),
      page_path: location.pathname,
    }, params || {});

    // GA4 via gtag
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, payload); } catch (e) {}
    }

    // Also push to dataLayer for GTM users
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));

    // Dev visibility (only if debug flag set)
    if (window.VV_DEBUG) console.log('[VVAnalytics]', eventName, payload);
  }

  function productParams(product, surface) {
    const variantId = product.variants && product.variants[0] && product.variants[0].id;
    const priceCents = (product.variants && product.variants[0] && product.variants[0].price)
      ? (typeof product.variants[0].price === 'string'
          ? Math.round(parseFloat(product.variants[0].price) * 100)
          : product.variants[0].price)
      : (typeof product.price === 'number' ? product.price : Math.round(parseFloat(product.price || 0) * 100));
    return {
      surface: surface || 'unknown',
      product_id: String(product.id || ''),
      product_title: product.title || '',
      product_type: product.product_type || product.type || '',
      vendor: product.vendor || '',
      variant_id: variantId ? String(variantId) : '',
      price_cents: priceCents,
      currency: 'CAD',
    };
  }

  function trackImpression(product, surface) {
    track('vv_upsell_view', productParams(product, surface));
  }
  function trackClick(product, surface) {
    track('vv_upsell_click', productParams(product, surface));
  }
  function trackAdd(product, surface) {
    track('vv_upsell_add', productParams(product, surface));
  }

  /**
   * Deterministic A/B bucketing.
   * getVariant('upsell_rules_v1', ['control', 'variant_a', 'variant_b'])
   * Returns same bucket for same visitor across sessions.
   */
  function getVariant(experimentKey, variants) {
    let buckets = {};
    try { buckets = JSON.parse(getCookie(AB_COOKIE) || '{}'); } catch (e) {}

    if (buckets[experimentKey]) return buckets[experimentKey];

    const visitor = getOrCreateVisitorId();
    const hash = hashString(visitor + ':' + experimentKey);
    const bucket = variants[hash % variants.length];

    buckets[experimentKey] = bucket;
    setCookie(AB_COOKIE, JSON.stringify(buckets), COOKIE_DAYS);

    track('vv_ab_assigned', { experiment: experimentKey, variant: bucket });
    return bucket;
  }

  function init() {
    getOrCreateVisitorId();

    // Auto-track impressions when upsell items become visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          if (el.dataset.vvImpressionFired) return;
          el.dataset.vvImpressionFired = '1';
          const productData = el.dataset.vvProduct;
          const surface = el.closest('[data-vv-surface]')?.dataset.vvSurface || 'unknown';
          if (productData) {
            try {
              trackImpression(JSON.parse(productData), surface);
            } catch (e) {}
          } else if (el.dataset.productId) {
            trackImpression({
              id: el.dataset.productId,
              title: el.dataset.productTitle || '',
              price: el.dataset.productPrice || 0
            }, surface);
          }
        }
      });
    }, { threshold: 0.5 });

    // Observe any existing or future upsell items
    function observeItems() {
      document.querySelectorAll('.vv-upsell__item:not([data-vv-observed])').forEach((el) => {
        el.dataset.vvObserved = '1';
        observer.observe(el);
      });
    }
    observeItems();
    // Re-observe after upsell re-renders
    document.addEventListener('vv:upsell:rendered', observeItems);
  }

  window.VVAnalytics = { track, trackImpression, trackClick, trackAdd, getVariant };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
